import {
    type CSSProperties,
    type ReactNode,
    useEffect
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    ThemeSwitcher
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Caption,
    Preview,
    Section
} from "../../components/content";
import {
    HeartIcon,
    StarIcon
} from "../../anatomy/icons";

const NAME = "ThemeSwitcher";

const USAGE_CODE = `<ThemeSwitcher />

<ThemeSwitcher
    form="segment"
/>`;

const VARIANTS_CODE = `<ThemeSwitcher
    variants={[
        {
            themeKey: "light",
            iconColor: "sun",
            icon: DayIcon,
            title: "Day"
        },
        {
            themeKey: "dark",
            iconColor: "moon",
            icon: NightIcon,
            title: "Night"
        }
    ]}
    isShowTitle={true}
/>`;

const FORMS = [
    "cycle",
    "menu",
    "segment"
] as const;

const DEMO_ITEM_STYLE: CSSProperties = {
    flexDirection: "column",
    alignItems: "center",
    display: "flex"
};

const DEMO_CONTENT_STYLE: CSSProperties = {
    justifyContent: "center",
    display: "flex",
    width: "100%"
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("themeSwitcherPage.formsTitle"),
        id: "forms"
    },
    {
        title: t("themeSwitcherPage.displayTitle"),
        id: "display"
    },
    {
        title: t("themeSwitcherPage.variantsTitle"),
        id: "variants"
    },
    {
        title: t("themeSwitcherPage.styleTitle"),
        id: "style"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const DemoItem = ({
    children,
    label
}: {
    children: ReactNode;
    label: string;
}) => <div
    style={DEMO_ITEM_STYLE}
>
    <div
        style={DEMO_CONTENT_STYLE}
    >
        {children}
    </div>
    <Caption>
        {label}
    </Caption>
</div>;

const ThemeSwitcherPage = () => {
    const {
        t
    } = useDocsI18n();

    useEffect(() => {
        return () => {
            let storedPreference: string | null = null;

            try {
                storedPreference = window.localStorage.getItem("ncore-docs-theme");
            } catch {
                storedPreference = null;
            }

            NCoreUIKitTheme.switch({
                themeKey: storedPreference === "light" || storedPreference === "dark" ? storedPreference : "system"
            });
        };
    }, []);

    return <Article>
        <ComponentHeader
            lead={t("themeSwitcherPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("themeSwitcherPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("themeSwitcherPage.overviewCaption")}
            >
                <ThemeSwitcher/>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("themeSwitcherPage.formsTitle")}
            id="forms"
        >
            <Paragraph>
                {t("themeSwitcherPage.formsText")}
            </Paragraph>
            <Preview>
                {
                    FORMS.map((form) => <DemoItem
                        label={form}
                        key={form}
                    >
                        <ThemeSwitcher
                            form={form}
                        />
                    </DemoItem>)
                }
            </Preview>
        </Section>
        <Section
            title={t("themeSwitcherPage.displayTitle")}
            id="display"
        >
            <Paragraph>
                {t("themeSwitcherPage.displayText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="isShowTitle"
                >
                    <ThemeSwitcher
                        isShowTitle={true}
                    />
                </DemoItem>
                <DemoItem
                    label="title"
                >
                    <ThemeSwitcher
                        title={t("themeSwitcherPage.customTitle")}
                    />
                </DemoItem>
                <DemoItem
                    label="isShowNextVariant"
                >
                    <ThemeSwitcher
                        isShowNextVariant={true}
                        isShowTitle={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("themeSwitcherPage.variantsTitle")}
            id="variants"
        >
            <Paragraph>
                {t("themeSwitcherPage.variantsText")}
            </Paragraph>
            <Preview>
                <ThemeSwitcher
                    variants={[
                        {
                            title: t("themeSwitcherPage.day"),
                            themeKey: "light",
                            iconColor: "sun",
                            icon: StarIcon
                        },
                        {
                            title: t("themeSwitcherPage.night"),
                            iconColor: "moon",
                            icon: HeartIcon,
                            themeKey: "dark"
                        }
                    ]}
                    isShowTitle={true}
                />
            </Preview>
            <CodeBlock
                code={VARIANTS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("themeSwitcherPage.styleTitle")}
            id="style"
        >
            <Paragraph>
                {t("themeSwitcherPage.styleText")}
            </Paragraph>
            <Preview>
                <DemoItem
                    label="variant=outline"
                >
                    <ThemeSwitcher
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="variant=ghost"
                >
                    <ThemeSwitcher
                        variant="ghost"
                    />
                </DemoItem>
                <DemoItem
                    label="type=primary"
                >
                    <ThemeSwitcher
                        type="primary"
                    />
                </DemoItem>
                <DemoItem
                    label="color=raised"
                >
                    <ThemeSwitcher
                        variant="outline"
                        color="raised"
                    />
                </DemoItem>
                <DemoItem
                    label="color=subtle"
                >
                    <ThemeSwitcher
                        variant="outline"
                        color="subtle"
                    />
                </DemoItem>
                <DemoItem
                    label="color=transparent"
                >
                    <ThemeSwitcher
                        color="transparent"
                        variant="outline"
                    />
                </DemoItem>
                <DemoItem
                    label="size=large"
                >
                    <ThemeSwitcher
                        size="large"
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=light"
                >
                    <ThemeSwitcher
                        customTheme={{
                            themeKey: "light"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="customTheme=dark"
                >
                    <ThemeSwitcher
                        customTheme={{
                            themeKey: "dark"
                        }}
                    />
                </DemoItem>
                <DemoItem
                    label="isDisabled"
                >
                    <ThemeSwitcher
                        isDisabled={true}
                    />
                </DemoItem>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("themeSwitcherPage.a11yName"),
                    t("themeSwitcherPage.a11yLabelInName"),
                    t("themeSwitcherPage.a11yMenu"),
                    t("themeSwitcherPage.a11ySegment"),
                    t("themeSwitcherPage.a11yDisabled"),
                    t("themeSwitcherPage.a11yCustom"),
                    t("themeSwitcherPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("themeSwitcherPage.propForm"),
                        type: "\"cycle\" | \"menu\" | \"segment\"",
                        defaultValue: "\"cycle\"",
                        name: "form"
                    },
                    {
                        description: t("themeSwitcherPage.propIsShowTitle"),
                        name: "isShowTitle",
                        type: "boolean"
                    },
                    {
                        description: t("themeSwitcherPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("themeSwitcherPage.propIsShowNextVariant"),
                        name: "isShowNextVariant",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "Array<{ themeKey, icon, title, iconColor, backgroundColor, borderColor }>",
                        description: t("themeSwitcherPage.propVariants"),
                        name: "variants"
                    },
                    {
                        type: "\"filled\" | \"tonal\" | \"outline\" | \"ghost\"",
                        description: t("themeSwitcherPage.propVariant"),
                        defaultValue: "\"tonal\"",
                        name: "variant"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("themeSwitcherPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "\"xSmall\" | \"small\" | \"medium\" | \"large\"",
                        description: t("themeSwitcherPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("themeSwitcherPage.propSpreadBehaviour"),
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        defaultValue: "\"baseline\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("themeSwitcherPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        type: "keyof ContainerContentColors | \"transparent\"",
                        description: t("themeSwitcherPage.propColor"),
                        name: "color"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("themeSwitcherPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("themeSwitcherPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    },
                    {
                        description: t("themeSwitcherPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("themeSwitcherPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default ThemeSwitcherPage;
